import {
  FocusEventHandler,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';

import { AIHintProps } from 'components/AIHint';
import { Typography } from 'components/typography';
import { Undef } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import FormInputWrapper from './FormInputWrapper';
import { Input } from './Input';

type RHFCommitOnBlurNumberInputProps = AIHintProps & {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  min?: number;
  step?: number;
  rules?: RegisterOptions;
  inlineDimensionUnit?: string;
  labelClassName?: string;
  inputClassName?: string;
  readOnly?: boolean;
  'data-testid'?: string;
};

function toDraftValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

function resolveValidationRules(
  label: string,
  required: boolean,
  min: Undef<number>,
  rules: Undef<RegisterOptions>
): Undef<RegisterOptions> {
  if (rules) {
    return rules;
  }

  const resolved: RegisterOptions = {};
  if (required) {
    resolved.required = `${label} is required`;
  }
  if (min != null) {
    resolved.min = {
      value: min,
      message:
        required && min >= 1
          ? `${label} is required`
          : `Must be at least ${min}`,
    };
  }

  if (Object.keys(resolved).length === 0) {
    return undefined;
  }

  return resolved;
}

function resolveMinConstraint(rules: Undef<RegisterOptions>): Undef<number> {
  const minRule = rules?.min;

  if (typeof minRule === 'number') {
    return minRule;
  }

  if (minRule && typeof minRule === 'object' && 'value' in minRule) {
    const minRuleValue = minRule.value;
    if (typeof minRuleValue === 'number') {
      return minRuleValue;
    }
  }

  return undefined;
}

type CommitOnBlurNumberFieldProps = {
  name: string;
  label: string;
  placeholder?: string;
  required: boolean;
  min: Undef<number>;
  step: number;
  inlineDimensionUnit?: string;
  labelClassName: string;
  inputClassName: string;
  readOnly: boolean;
  dataTestId?: string;
  fieldValue: unknown;
  onCommit: (value: Undef<number>) => void;
  onBlurField: () => void;
  onValidateOnBlur: () => void;
  wrapperProps: Omit<AIHintProps, 'name'>;
};

function CommitOnBlurNumberField({
  name,
  label,
  placeholder,
  required,
  min,
  step,
  inlineDimensionUnit,
  labelClassName,
  inputClassName,
  readOnly,
  dataTestId,
  fieldValue,
  onCommit,
  onBlurField,
  onValidateOnBlur,
  wrapperProps,
}: CommitOnBlurNumberFieldProps) {
  const [draftValue, setDraftValue] = useState<string>(
    toDraftValue(fieldValue)
  );
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (isFocusedRef.current) {
      return;
    }
    setDraftValue(toDraftValue(fieldValue));
  }, [fieldValue]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = () => {
    isFocusedRef.current = false;
    const trimmedDraftValue = draftValue.trim();
    const hasCommittedValue = toDraftValue(fieldValue).trim() !== '';

    if (trimmedDraftValue === '') {
      if (!hasCommittedValue) {
        onBlurField();
        if (required) {
          onValidateOnBlur();
        }

        setDraftValue('');

        return;
      }

      onCommit(undefined);
      onBlurField();
      setDraftValue('');

      return;
    }

    const parsedValue = Number(trimmedDraftValue);
    const isBelowMinimum = min != null && parsedValue < min;
    if (!Number.isFinite(parsedValue) || isBelowMinimum) {
      onCommit(undefined);
      onBlurField();
      setDraftValue('');

      return;
    }

    onCommit(parsedValue);
    onBlurField();
    setDraftValue(String(parsedValue));
  };

  return (
    <FormInputWrapper
      name={name}
      label={label}
      required={required}
      className={labelClassName}
      {...wrapperProps}
    >
      <div className='relative'>
        <Input
          readOnly={readOnly}
          placeholder={placeholder}
          type='number'
          min={min}
          step={step}
          value={draftValue}
          onFocus={() => {
            isFocusedRef.current = true;
          }}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          data-testid={dataTestId}
          className={cn(inputClassName, inlineDimensionUnit ? 'pr-8' : '')}
        />
        {inlineDimensionUnit && (
          <Typography
            variant='body-xs'
            weight='medium'
            textColor='muted'
            className='absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none'
          >
            {inlineDimensionUnit}
          </Typography>
        )}
      </div>
    </FormInputWrapper>
  );
}

// React Hook Form number input that uses a two-phase commit model:
// keeps a local draft while typing, then commits to form state only on blur.
export function RHFCommitOnBlurNumberInput({
  name,
  label,
  placeholder,
  required = false,
  min,
  step = 1,
  rules,
  inlineDimensionUnit,
  labelClassName = '',
  inputClassName = '',
  readOnly = false,
  'data-testid': dataTestId,
  ...props
}: RHFCommitOnBlurNumberInputProps) {
  const { control, trigger } = useFormContext();

  const validationRules = resolveValidationRules(label, required, min, rules);
  const resolvedMin = resolveMinConstraint(validationRules);

  return (
    <Controller
      name={name as FieldPath<FieldValues>}
      control={control}
      rules={validationRules}
      render={({ field }) => (
        <CommitOnBlurNumberField
          name={name}
          label={label}
          placeholder={placeholder}
          required={required}
          min={resolvedMin}
          step={step}
          inlineDimensionUnit={inlineDimensionUnit}
          labelClassName={labelClassName}
          inputClassName={inputClassName}
          readOnly={readOnly}
          dataTestId={dataTestId}
          fieldValue={field.value}
          onCommit={field.onChange}
          onBlurField={field.onBlur}
          onValidateOnBlur={() => {
            void trigger(name);
          }}
          wrapperProps={props}
        />
      )}
    />
  );
}
